import { z } from 'zod';

// Property Edit Schema
export const editPropertySchema = z
  .object({
    name: z
      .string()
      .min(1, 'Property name is required')
      .min(3, 'Property name must be at least 3 characters')
      .max(255, 'Property name must be less than 255 characters'),
    property_type_id: z.string().min(1, 'Property type is required'),
    stars: z
      .number()
      .min(1, 'Stars must be at least 1')
      .max(5, 'Stars must be at most 5'),
    location: z
      .string()
      .min(1, 'Location is required')
      .min(3, 'Location must be at least 3 characters')
      .max(255, 'Location must be less than 255 characters'),
    contact_person: z
      .string()
      .min(1, 'Contact person name is required')
      .min(3, 'Contact person name must be at least 3 characters')
      .max(255, 'Contact person name must be less than 255 characters'),
    contact_email: z
      .string()
      .min(1, 'Contact email is required')
      .email('Please enter a valid email address'),
    contact_phone: z
      .string()
      .min(1, 'Contact phone is required')
      .min(7, 'Phone number must be at least 7 characters')
      .max(20, 'Phone number must be less than 20 characters'),
    contract_start: z.string().min(1, 'Contract start date is required'),
    contract_end: z.string().min(1, 'Contract end date is required'),
    is_active: z.boolean(),
    photoBase64: z.string().optional().nullable(),
    contractScope: z.enum(['full', 'limited']).optional(),
    selectedDepartmentTouchpoints: z
      .array(
        z.object({
          departmentId: z.string(),
          touchpointIds: z.array(z.string()),
        })
      )
      .optional(),
    property_scope: z
      .array(
        z.object({
          property_type_id: z.number(),
          department_id: z.number(),
          touchpoint_id: z.number(),
        })
      )
      .optional(),
  })
  .refine(
    data => {
      try {
        return new Date(data.contract_start) < new Date(data.contract_end);
      } catch {
        return false;
      }
    },
    {
      message: 'Contract end date must be after start date',
      path: ['contract_end'],
    }
  )
  .refine(
    data => {
      // If contract scope is limited, must have at least one touchpoint selected
      if (data.contractScope === 'limited') {
        if (
          !data.selectedDepartmentTouchpoints ||
          data.selectedDepartmentTouchpoints.length === 0
        ) {
          return false;
        }
        const hasAnyTouchpoint = data.selectedDepartmentTouchpoints.some(
          dept => dept.touchpointIds && dept.touchpointIds.length > 0
        );
        return hasAnyTouchpoint;
      }
      return true;
    },
    {
      message: 'Please select at least one touchpoint for limited access',
      path: ['selectedDepartmentTouchpoints'],
    }
  );

export type EditPropertyData = z.infer<typeof editPropertySchema>;

export const createPropertySchema = z.object({
  name: z.string().min(1, 'Property name is required'),
  property_type_id: z.string().min(1, 'Property type is required'),
  location: z.string().min(1, 'Location is required'),
  stars: z
    .number()
    .min(1, 'Stars must be between 1 and 5')
    .max(5, 'Stars must be between 1 and 5'),
  photoUrl: z.string().optional(),
});

export type CreatePropertyData = z.infer<typeof createPropertySchema>;

// Step 1: Property Basic Information Schema
export const propertyBasicInfoSchema = z.object({
  name: z
    .string()
    .min(1, 'Property name is required')
    .min(3, 'Property name must be at least 3 characters')
    .max(255, 'Property name must be less than 255 characters'),

  property_type_id: z.string().min(1, 'Property type is required'),

  stars: z
    .number()
    .min(1, 'Stars must be at least 1')
    .max(5, 'Stars must be at most 5'),

  location: z
    .string()
    .min(1, 'Location is required')
    .min(3, 'Location must be at least 3 characters')
    .max(255, 'Location must be less than 255 characters'),

  photoBase64: z.string().optional().nullable(),

  contactPerson: z
    .string()
    .min(1, 'Contact person name is required')
    .min(3, 'Contact person name must be at least 3 characters')
    .max(255, 'Contact person name must be less than 255 characters'),

  contactEmail: z
    .string()
    .min(1, 'Contact email is required')
    .email('Please enter a valid email address'),

  contactPhone: z
    .string()
    .min(1, 'Contact phone is required')
    .min(7, 'Phone number must be at least 7 characters')
    .max(20, 'Phone number must be less than 20 characters'),
});

export type PropertyBasicInfoData = z.infer<typeof propertyBasicInfoSchema>;

// Step 2: Contract Settings Schema
export const propertyContractSettingsSchema = z
  .object({
    contractScope: z.enum(['full', 'limited'], {
      required_error: 'Contract scope is required',
    }),

    contractStartDate: z
      .string()
      .min(1, 'Contract start date is required')
      .refine(date => !isNaN(Date.parse(date)), 'Invalid date format'),

    contractEndDate: z
      .string()
      .min(1, 'Contract end date is required')
      .refine(date => !isNaN(Date.parse(date)), 'Invalid date format'),
  })
  .refine(
    data => new Date(data.contractStartDate) < new Date(data.contractEndDate),
    {
      message: 'Contract end date must be after start date',
      path: ['contractEndDate'],
    }
  );

export type PropertyContractSettingsData = z.infer<
  typeof propertyContractSettingsSchema
>;

// Step 2: Contract Scope Selection Schema (for limited scope)
export const propertyContractScopeSchema = z.object({
  selectedDepartments: z
    .array(z.string())
    .min(1, 'At least one department must be selected')
    .optional(),

  selectedTouchpoints: z
    .array(z.string())
    .min(1, 'At least one touchpoint must be selected')
    .optional(),
});

export type PropertyContractScopeData = z.infer<
  typeof propertyContractScopeSchema
>;

// Step 2: Contract Settings and Scope Selection Schema (Combined)
export const propertyStep2Schema = z
  .object({
    contractScope: z.enum(['full', 'limited'], {
      required_error: 'Contract scope is required',
    }),
    contractStartDate: z.string().min(1, 'Contract start date is required'),
    contractEndDate: z.string().min(1, 'Contract end date is required'),
    // Each selectedDepartmentTouchpoints is structured as: { departmentId, touchpointIds[] }
    selectedDepartmentTouchpoints: z
      .array(
        z.object({
          departmentId: z.string(),
          touchpointIds: z.array(z.string()),
        })
      )
      .optional(),
  })
  .refine(
    data => {
      try {
        return (
          new Date(data.contractStartDate) < new Date(data.contractEndDate)
        );
      } catch {
        return false;
      }
    },
    {
      message: 'Contract end date must be after start date',
      path: ['contractEndDate'],
    }
  )
  .refine(
    data => {
      // If contract scope is limited, must have at least one touchpoint selected
      if (data.contractScope === 'limited') {
        if (
          !data.selectedDepartmentTouchpoints ||
          data.selectedDepartmentTouchpoints.length === 0
        ) {
          return false;
        }
        // Check that at least one department has at least one touchpoint selected
        const hasAnyTouchpoint = data.selectedDepartmentTouchpoints.some(
          dept => dept.touchpointIds && dept.touchpointIds.length > 0
        );
        return hasAnyTouchpoint;
      }
      return true;
    },
    {
      message: 'Please select at least one touchpoint for limited access',
      path: ['selectedDepartmentTouchpoints'],
    }
  );

export type PropertyStep2Data = z.infer<typeof propertyStep2Schema>;

// Complete Property Creation Form Schema (All Steps Combined)
export const propertyCreateFormSchema = z
  .object({
    // Step 1: Basic Info
    name: z
      .string()
      .min(1, 'Property name is required')
      .min(3, 'Property name must be at least 3 characters')
      .max(255, 'Property name must be less than 255 characters'),

    property_type_id: z.string().min(1, 'Property type is required'),

    stars: z
      .number()
      .min(1, 'Stars must be at least 1')
      .max(5, 'Stars must be at most 5'),

    location: z
      .string()
      .min(1, 'Location is required')
      .min(3, 'Location must be at least 3 characters')
      .max(255, 'Location must be less than 255 characters'),

    photoBase64: z.string().optional().nullable(),

    // Step 1: Contact Info
    contactPerson: z
      .string()
      .min(1, 'Contact person name is required')
      .min(3, 'Contact person name must be at least 3 characters')
      .max(255, 'Contact person name must be less than 255 characters'),

    contactEmail: z
      .string()
      .min(1, 'Contact email is required')
      .email('Please enter a valid email address'),

    contactPhone: z
      .string()
      .min(1, 'Contact phone is required')
      .min(7, 'Phone number must be at least 7 characters')
      .max(20, 'Phone number must be less than 20 characters'),

    // Step 2: Contract Settings
    contractScope: z.enum(['full', 'limited'], {
      required_error: 'Contract scope is required',
    }),

    contractStartDate: z.string().min(1, 'Contract start date is required'),

    contractEndDate: z.string().min(1, 'Contract end date is required'),

    // Step 2: Scope Selection (Department-Touchpoint pairs)
    selectedDepartmentTouchpoints: z
      .array(
        z.object({
          departmentId: z.string(),
          touchpointIds: z.array(z.string()),
        })
      )
      .optional(),
  })
  .refine(
    data => {
      try {
        return (
          new Date(data.contractStartDate) < new Date(data.contractEndDate)
        );
      } catch {
        return false;
      }
    },
    {
      message: 'Contract end date must be after start date',
      path: ['contractEndDate'],
    }
  );

export type PropertyCreateFormData = z.infer<typeof propertyCreateFormSchema>;
